
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Clock, Users, ChevronRight, CalendarCheck, Plus, ArrowRight } from "lucide-react";

export const MobileAppointmentSection = () => {
  const navigatePath = "/rendez-vous-mobile";
  
  return (
    <div className="px-4 py-6 space-y-4">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">Gestion des Rendez-vous</h2>
        <p className="text-muted-foreground">
          Planifiez et gérez vos consultations médicales en toute simplicité
        </p>
      </div>
      
      <Card className="overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-100 border-none shadow-lg hover:shadow-xl transition-all duration-300">
        <CardContent className="p-0">
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="space-y-1">
                <span className="text-indigo-900 text-sm font-medium">Vos rendez-vous</span>
                <h3 className="text-2xl font-bold text-indigo-700">
                  Prochaine consultation
                </h3>
              </div>
              <div className="bg-indigo-200/70 text-indigo-800 p-3 rounded-full animate-pulse">
                <Calendar className="h-6 w-6" />
              </div>
            </div>
            
            {/* Appointment Preview Card */}
            <div className="bg-white/80 rounded-xl p-4 shadow-sm mb-4 transform hover:scale-[1.02] transition-transform duration-200">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 rounded-full p-2">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Dr. Martin Dubois</h4>
                  <p className="text-sm text-gray-500">Consultation générale</p>
                  <div className="flex items-center gap-4 mt-2">
                    <div className="flex items-center gap-1 text-xs text-blue-600">
                      <Calendar className="h-3 w-3" />
                      <span>15 Mai 2024</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-blue-600">
                      <Clock className="h-3 w-3" />
                      <span>14:30</span>
                    </div>
                  </div>
                </div>
                <div className="bg-green-100 text-green-800 rounded-full px-3 py-1 text-xs font-medium">
                  Confirmé
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-white/60 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-indigo-700">3</div>
                <div className="text-xs text-indigo-600">À venir</div>
              </div>
              <div className="bg-white/60 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-green-700">12</div>
                <div className="text-xs text-green-600">Terminés</div>
              </div>
              <div className="bg-white/60 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-orange-700">1</div>
                <div className="text-xs text-orange-600">En attente</div>
              </div>
            </div>
            
            {/* Action Button */}
            <Button
              asChild
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-lg transition-all duration-200 group"
            >
              <Link to={navigatePath}>
                <CalendarCheck className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
                Gérer mes rendez-vous
                <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
          
          {/* Bottom Section */}
          <div className="bg-indigo-100/80 p-4 flex justify-between items-center border-t border-indigo-200/50">
            <div className="flex items-center gap-2 text-sm text-indigo-800">
              <Clock className="h-4 w-4" />
              <span>Rappels automatiques</span>
            </div>
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="text-indigo-800 hover:text-indigo-900 hover:bg-indigo-200/50"
            >
              <Link to={navigatePath}>
                Voir tout <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Quick Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <Button 
          asChild
          variant="outline"
          className="justify-start h-auto py-4 font-normal bg-white hover:bg-blue-50 border-blue-200 group transition-all duration-200"
        >
          <Link to={navigatePath}>
            <Plus className="h-5 w-5 mr-3 text-blue-600 group-hover:scale-110 transition-transform" />
            <div className="text-left">
              <div className="font-medium text-gray-900">Nouveau</div>
              <span className="text-xs text-muted-foreground">Prendre RDV</span>
            </div>
          </Link>
        </Button>
        
        <Button 
          asChild
          variant="outline"
          className="justify-start h-auto py-4 font-normal bg-white hover:bg-blue-50 border-blue-200 group transition-all duration-200"
        >
          <Link to={navigatePath}>
            <Calendar className="h-5 w-5 mr-3 text-blue-600 group-hover:scale-110 transition-transform" />
            <div className="text-left">
              <div className="font-medium text-gray-900">Planning</div>
              <span className="text-xs text-muted-foreground">Voir agenda</span>
            </div>
          </Link>
        </Button>
      </div>

      {/* Floating Animation Elements */}
      <div className="relative overflow-hidden">
        <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-blue-200/30 animate-pulse" style={{animationDuration: "3s"}}></div>
        <div className="absolute top-8 left-1/3 w-8 h-8 rounded-full bg-indigo-200/40 animate-pulse" style={{animationDuration: "4s", animationDelay: "1s"}}></div>
        <div className="absolute bottom-4 right-1/4 w-6 h-6 rounded-full bg-purple-200/40 animate-pulse" style={{animationDuration: "3.5s", animationDelay: "0.5s"}}></div>
      </div>
    </div>
  );
};
